import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import type {
  ResolvedExperienceShell,
  ResolvedRouteExperienceDescriptor,
  RouteId,
} from '@finance/contracts';
import { authAtom } from '../auth/auth.atoms';
import { useExperienceShell } from './useResolvedExperience';

export interface ExperienceContextValue {
  readonly shell?: ResolvedExperienceShell;
  readonly routes: readonly ResolvedRouteExperienceDescriptor[];
  readonly loading: boolean;
  readonly error?: Error;
  readonly getRoute: (routeId: RouteId | string | undefined) => ResolvedRouteExperienceDescriptor | undefined;
  readonly getRoutePath: (routeId: RouteId | string | undefined) => string | undefined;
  readonly refresh: () => Promise<unknown>;
}

const ExperienceContext = createContext<ExperienceContextValue | undefined>(undefined);

export function ExperienceProvider({ children }: { children: ReactNode }) {
  const auth = useAtomValue(authAtom);
  const query = useExperienceShell(auth.isAuthenticated && !auth.loading);
  const routes = query.data?.routes ?? [];
  const routeMap = useMemo(
    () => new Map(routes.map((entry) => [String(entry.route.routeId), entry])),
    [routes],
  );
  const getRoute = useCallback(
    (routeId: RouteId | string | undefined) => routeId ? routeMap.get(String(routeId)) : undefined,
    [routeMap],
  );
  const getRoutePath = useCallback(
    (routeId: RouteId | string | undefined) => getRoute(routeId)?.route.path,
    [getRoute],
  );
  const value = useMemo<ExperienceContextValue>(() => ({
    shell: query.data,
    routes,
    loading: query.isLoading || auth.loading,
    error: query.error instanceof Error ? query.error : undefined,
    getRoute,
    getRoutePath,
    refresh: query.refetch,
  }), [auth.loading, getRoute, getRoutePath, query.data, query.error, query.isLoading, query.refetch, routes]);

  return <ExperienceContext.Provider value={value}>{children}</ExperienceContext.Provider>;
}

export function useExperience(): ExperienceContextValue {
  const value = useContext(ExperienceContext);
  if (!value) throw new Error('useExperience must be used inside ExperienceProvider');
  return value;
}
